<script lang="ts" setup>
import type { TFilterItem, TFilterItemCategory } from '~/layers/catalog/data/types'
import type { PropType } from 'vue'

import { useCatalogFilter } from '~/layers/catalog/composables/catalogFilter'
import { useCatalogStore } from '~/layers/catalog/store/catalog'

const props = defineProps({
  filter: {
    type: Object as PropType<TFilterItemCategory & TFilterItem>,
    required: true,
  },
})

const store = useCatalogStore()
const service = useCatalogFilter()

const isOpenedMore = ref(false)
const isShowingMoreButton = props.filter?.items.length > 10

const items = computed(() => {
  if (useMediaQuery().state.xs || useMediaQuery().state.sm || useMediaQuery().state.md) {
    return props.filter.items
  }

  if (isOpenedMore.value) {
    return props.filter.items
  }

  return props.filter.items.slice(0, 10)
})

const isChecked = computed(() => (code: string | null) => {
  if (!code) {
    return store.selectedCategories.length === 0
  }

  return store.selectedCategories.includes(code)
})

const isDisabled = (code: string | null, disabled: boolean) => {
  return Boolean(code && disabled && !isChecked.value(code))
}

const toggle = () => {
  isOpenedMore.value = !isOpenedMore.value
}

const resetCategories = () => {
  service.toggleCategory(null)
}

const change = (code: string | null, disabled: boolean) => {
  if (isDisabled(code, disabled)) {
    return
  }

  if (!code) {
    resetCategories()
    return
  }
  const wasChecked = isChecked.value(code)
  const selectedCategories = wasChecked
    ? store.selectedCategories.filter((c) => code != c)
    : [...store.selectedCategories, code]

  const sortedCodes = items.value
    .map((item) => item.code)
    .filter((code): code is string => code !== null && selectedCategories.includes(code))

  const totalCategories = props.filter.items.filter((v) => v.code).length
  if (sortedCodes.length === totalCategories) {
    resetCategories()
    return
  }

  service.setSelectedCategories(sortedCodes)
}
</script>

<template>
  <client-only>
    <button
      v-for="(item, index) in items"
      :key="index"
      class="filters-catalog__link filters__value"
      :class="{ active: isChecked(item.code), disabledAndChecked: item.disabled && isChecked(item.code) }"
      :disabled="isDisabled(item.code, item.disabled)"
      @click="change(item.code, item.disabled)"
    >
      {{ item.name }}
    </button>
  </client-only>

  <button v-if="isShowingMoreButton" class="filters__button-show" @click="toggle">
    <span v-if="!isOpenedMore" class="filters__button-show_more">Ещё {{ filter.title.toLowerCase() }}</span>
    <span v-else class="filters__button-show_hide">Свернуть</span>
  </button>
</template>

<style lang="scss">
@use '/assets/scss/components/filters-catalog.scss' as *;
</style>
